/**
 * Admin Hooks
 *
 * React Query hooks for admin dashboard (Director only).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import * as adminService from "../services/adminService";
import type {
  BulkClassPriceRequest,
  CreateClassGroupRequest,
  CreateContractOverrideRequest,
  CreateLessonRequest,
  CreateLevelRequest,
  CreateStudentRequest,
  CreateTeacherRequest,
  MoveStudentRequest,
  UpdateClassGroupRequest,
  UpdateContractRequest,
  UpdateLessonRequest,
  UpdateLevelRequest,
  UpdateStudentRequest,
  UpdateTeacherRequest,
} from "../types/admin";

// ============================================================================
// TEACHERS
// ============================================================================

export function useAdminTeachers(params?: { isActive?: boolean }) {
  return useQuery({
    queryKey: ["admin", "teachers", params],
    queryFn: () => adminService.getTeachers(params),
    staleTime: 1000 * 60 * 2,
  });
}

export function useAdminTeacher(id: string) {
  return useQuery({
    queryKey: ["admin", "teachers", id],
    queryFn: () => adminService.getTeacher(id),
    enabled: !!id,
  });
}

export function useCreateTeacher() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTeacherRequest) =>
      adminService.createTeacher(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "teachers"] });
      toast.success("Professor criado com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao criar professor");
    },
  });
}

export function useUpdateTeacher() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTeacherRequest }) =>
      adminService.updateTeacher(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "teachers"] });
      toast.success("Professor atualizado com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao atualizar professor");
    },
  });
}

export function useDeleteTeacher() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.deleteTeacher(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "teachers"] });
      toast.success("Teacher deactivated!");
    },
    onError: () => {
      toast.error("Error deactivating teacher");
    },
  });
}

export function useReactivateTeacher() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.reactivateTeacher(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "teachers"] });
      toast.success("Teacher reactivated!");
    },
    onError: () => {
      toast.error("Error reactivating teacher");
    },
  });
}

export function useResetTeacherPassword() {
  return useMutation({
    mutationFn: (id: string) => adminService.resetTeacherPassword(id),
    onSuccess: () => {
      toast.success("Senha resetada com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao resetar senha");
    },
  });
}

export function useHardDeleteTeacher() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.hardDeleteTeacher(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "teachers"] });
      toast.success("Teacher permanently deleted!");
    },
    onError: () => {
      toast.error("Error deleting teacher");
    },
  });
}

// ============================================================================
// STUDENTS
// ============================================================================

export function useAdminStudents(params?: {
  classGroup?: string;
  level?: string;
  isActive?: boolean;
}) {
  return useQuery({
    queryKey: ["admin", "students", params],
    queryFn: () => adminService.getStudents(params),
    staleTime: 1000 * 60 * 2,
  });
}

export function useAdminStudent(id: string) {
  return useQuery({
    queryKey: ["admin", "students", id],
    queryFn: () => adminService.getStudent(id),
    enabled: !!id,
  });
}

export function useCreateStudent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateStudentRequest) =>
      adminService.createStudent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "students"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "classes"] });
      toast.success("Student created successfully!");
    },
    onError: () => {
      toast.error("Error creating student, please check the data and try again");
    },
  });
}

export function useUpdateStudent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStudentRequest }) =>
      adminService.updateStudent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "students"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "classes"] });
      toast.success("Student updated successfully!");
    },
    onError: () => {
      toast.error("Error updating student, please check the data and try again");
    },
  });
}

export function useDeleteStudent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.deleteStudent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "students"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "classes"] });
      toast.success("Student deactivated!");
    },
    onError: () => {
      toast.error("Error deactivating student");
    },
  });
}

export function useReactivateStudent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.reactivateStudent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "students"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "classes"] });
      toast.success("Student reactivated!");
    },
    onError: () => {
      toast.error("Error reactivating student");
    },
  });
}

export function useHardDeleteStudent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.hardDeleteStudent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "students"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "classes"] });
      toast.success("Student permanently deleted!");
    },
    onError: () => {
      toast.error("Error deleting student");
    },
  });
}

export function useMoveStudentToClass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MoveStudentRequest }) =>
      adminService.moveStudentToClass(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "students"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "classes"] });
      toast.success("Aluno movido com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao mover aluno");
    },
  });
}

export function useResetStudentPassword() {
  return useMutation({
    mutationFn: (id: string) => adminService.resetStudentPassword(id),
    onSuccess: () => {
      toast.success("Senha resetada com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao resetar senha");
    },
  });
}

// ============================================================================
// CLASS GROUPS
// ============================================================================

export function useAdminClassGroups(params?: {
  level?: string;
  teacher?: string;
  isActive?: boolean;
}) {
  return useQuery({
    queryKey: ["admin", "classes", params],
    queryFn: () => adminService.getClassGroups(params),
    staleTime: 1000 * 60 * 2,
  });
}

export function useAdminClassGroup(id: string) {
  return useQuery({
    queryKey: ["admin", "classes", id],
    queryFn: () => adminService.getClassGroup(id),
    enabled: !!id,
  });
}

export function useCreateClassGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateClassGroupRequest) =>
      adminService.createClassGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "classes"] });
      queryClient.invalidateQueries({ queryKey: ["teacher", "classes"] });
      toast.success("Turma criada com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao criar turma");
    },
  });
}

export function useUpdateClassGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClassGroupRequest }) =>
      adminService.updateClassGroup(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "classes"] });
      queryClient.invalidateQueries({ queryKey: ["teacher", "classes"] });
      toast.success("Turma atualizada com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao atualizar turma");
    },
  });
}

export function useDeleteClassGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.deleteClassGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "classes"] });
      queryClient.invalidateQueries({ queryKey: ["teacher", "classes"] });
      toast.success("Class deactivated!");
    },
    onError: () => {
      toast.error("Error deactivating class");
    },
  });
}

export function useReactivateClassGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.reactivateClassGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "classes"] });
      queryClient.invalidateQueries({ queryKey: ["teacher", "classes"] });
      toast.success("Class reactivated!");
    },
    onError: () => {
      toast.error("Error reactivating class");
    },
  });
}

export function useHardDeleteClassGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.hardDeleteClassGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "classes"] });
      queryClient.invalidateQueries({ queryKey: ["teacher", "classes"] });
      toast.success("Class permanently deleted!");
    },
    onError: () => {
      toast.error("Error deleting class");
    },
  });
}

export function useBulkPriceClasses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkClassPriceRequest) =>
      adminService.bulkPriceClasses(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "classes"] });
      toast.success(`${res.updated} turma(s) atualizada(s)!`);
    },
    onError: () => {
      toast.error("Erro ao atualizar preços");
    },
  });
}

// ============================================================================
// LEVELS
// ============================================================================

export function useAdminLevels() {
  return useQuery({
    queryKey: ["admin", "levels"],
    queryFn: adminService.getLevels,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useCreateLevel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLevelRequest) => adminService.createLevel(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "levels"] });
      toast.success("Nível criado com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao criar nível");
    },
  });
}

export function useUpdateLevel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLevelRequest }) =>
      adminService.updateLevel(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "levels"] });
      toast.success("Nível atualizado com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao atualizar nível");
    },
  });
}

export function useDeleteLevel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.deleteLevel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "levels"] });
      toast.success("Nível removido!");
    },
    onError: () => {
      toast.error("Erro ao remover nível (verifique lessons/turmas vinculadas)");
    },
  });
}

// ============================================================================
// LESSONS
// ============================================================================

export function useAdminLessons(params?: { level?: string }) {
  return useQuery({
    queryKey: ["admin", "lessons", params],
    queryFn: () => adminService.getLessons(params),
    staleTime: 1000 * 60 * 2,
  });
}

export function useCreateLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLessonRequest) => adminService.createLesson(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "lessons"] });
      toast.success("Lesson criada com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao criar lesson");
    },
  });
}

export function useUpdateLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLessonRequest }) =>
      adminService.updateLesson(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "lessons"] });
      toast.success("Lesson atualizada com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao atualizar lesson");
    },
  });
}

export function useDeleteLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.deleteLesson(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "lessons"] });
      toast.success("Lesson removida!");
    },
    onError: () => {
      toast.error("Erro ao remover lesson");
    },
  });
}

export function useReorderLessons() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      level,
      items,
    }: {
      level: string;
      items: { id: string; order: number }[];
    }) => adminService.reorderLessons(level, items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "lessons"] });
    },
    onError: () => {
      toast.error("Erro ao reordenar lessons");
    },
  });
}

// ============================================================================
// CONTRACTS
// ============================================================================

export function useAdminContracts() {
  return useQuery({
    queryKey: ["admin", "contracts"],
    queryFn: adminService.getContracts,
    staleTime: 1000 * 60 * 5,
  });
}

export function useUpdateContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateContractRequest }) =>
      adminService.updateContract(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "contracts"] });
      toast.success("Contrato atualizado! Alunos afetados vão assinar de novo.");
    },
    onError: () => {
      toast.error("Erro ao atualizar contrato");
    },
  });
}

export function useCreateContractOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateContractOverrideRequest) =>
      adminService.createContractOverride(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "contracts"] });
      toast.success("Contrato da turma criado! Os alunos dessa turma vão assinar.");
    },
    onError: () => {
      toast.error("Erro ao criar contrato da turma");
    },
  });
}

export function useDeleteContractOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.deleteContractOverride(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "contracts"] });
      toast.success("Override removido. A turma volta ao contrato do tipo.");
    },
    onError: () => {
      toast.error("Erro ao remover override");
    },
  });
}

// ============================================================================
// CLASS LESSON PLAN (per-class override of the level's lessons)
// ============================================================================

export function useClassLessonPlan(classGroupId: string | undefined) {
  return useQuery({
    queryKey: ["admin", "class-lesson-plan", classGroupId],
    queryFn: () => adminService.getClassLessonPlan(classGroupId as string),
    enabled: !!classGroupId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useCustomizeClassLessonPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (classGroupId: string) =>
      adminService.customizeClassLessonPlan(classGroupId),
    onSuccess: (_data, classGroupId) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "class-lesson-plan", classGroupId],
      });
      toast.success("Turma customizada a partir do nível.");
    },
    onError: () => {
      toast.error("Erro ao customizar a turma");
    },
  });
}

export function useResetClassLessonPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (classGroupId: string) =>
      adminService.resetClassLessonPlan(classGroupId),
    onSuccess: (_data, classGroupId) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "class-lesson-plan", classGroupId],
      });
      toast.success("Turma voltou ao plano do nível.");
    },
    onError: () => {
      toast.error("Erro ao resetar a turma");
    },
  });
}

export function useReorderClassLessonPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      classGroupId,
      items,
    }: {
      classGroupId: string;
      items: { id: string; order: number }[];
    }) => adminService.reorderClassLessonPlan(classGroupId, items),
    onSuccess: (_data, { classGroupId }) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "class-lesson-plan", classGroupId],
      });
    },
    onError: () => {
      toast.error("Erro ao reordenar o plano da turma");
    },
  });
}

export function useSetClassLessonPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      classGroupId,
      items,
    }: {
      classGroupId: string;
      items: { lesson: string; order: number; monthAvailable: number }[];
    }) => adminService.setClassLessonPlan(classGroupId, items),
    onSuccess: (_data, { classGroupId }) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "class-lesson-plan", classGroupId],
      });
      toast.success("Plano da turma salvo! Só afeta esta turma.");
    },
    onError: () => {
      toast.error("Erro ao salvar o plano da turma");
    },
  });
}

export function useAddClassLessonPlanItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      classGroup: string;
      lesson: string;
      order: number;
      monthAvailable: number;
    }) => adminService.addClassLessonPlanItem(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "class-lesson-plan", variables.classGroup],
      });
      toast.success("Lesson adicionada ao plano da turma.");
    },
    onError: () => {
      toast.error("Erro ao adicionar lesson");
    },
  });
}

export function useUpdateClassLessonPlanItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      classGroupId: string;
      data: { lesson?: string; monthAvailable?: number; order?: number };
    }) => adminService.updateClassLessonPlanItem(id, data),
    onSuccess: (_data, { classGroupId }) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "class-lesson-plan", classGroupId],
      });
    },
    onError: () => {
      toast.error("Erro ao atualizar o plano da turma");
    },
  });
}

export function useRemoveClassLessonPlanItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; classGroupId: string }) =>
      adminService.removeClassLessonPlanItem(id),
    onSuccess: (_data, { classGroupId }) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "class-lesson-plan", classGroupId],
      });
      toast.success("Lesson removida do plano da turma.");
    },
    onError: () => {
      toast.error("Erro ao remover lesson");
    },
  });
}
